import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChatStatus } from "ai";
import {
  fetchSessionNavigation,
  fetchTurnHeadings,
  probeNavigationCapabilities,
  type NavigationCapabilities,
  type NavigationHeading,
  type NavigationTurn,
  type SessionNavigationResponse,
} from "@/api/conversation-navigation";
import { useChildSessions } from "@/hooks/use-child-sessions";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import { extractConversationHeadings } from "@/lib/conversation-headings";
import { conversationTargetFragment, isCanonicalConversationId, parseConversationTarget, type ConversationTarget } from "@/lib/conversation-target";
import { compareMessages } from "@/lib/message-store";
import { buildRenderItems } from "@/lib/render-items";
import type { ChildSessionSummary, ThreadAnchorRef } from "@/lib/session";
import type { ConversationBootstrap } from "@/api/conversation-bootstrap";
import type { CapabilityProbeResult } from "@/api/conversation-navigation";
import { cacheScope } from "@/lib/ui-cache";

type FetchWithAuth = (url: string, init: RequestInit) => Promise<Response>;

export interface TimelineHeading extends NavigationHeading {
  loaded: boolean;
}

const EMPTY_HEADINGS: TimelineHeading[] = [];
const capabilitiesByGeneration = new Map<string, Extract<CapabilityProbeResult, { state: "supported" | "unsupported" }>>();
const capabilityCacheKey = (generation: number) => `${cacheScope()}:${generation}`;
/** Clear deployment capability snapshots after an explicit test or identity reset. */
export function clearNavigationCapabilitiesCache(): void { capabilitiesByGeneration.clear(); }

export interface TimelineReply {
  messageId: string;
  label: string;
}

export interface TimelineThread {
  child: ChildSessionSummary;
  sourceMessageId: string | null;
  sourceTurnId: string | null;
}

export interface TimelineTurn {
  turnId: string;
  startMessageId: string;
  startedAt: string;
  ordinal: number | null;
  pending: boolean;
  loaded: boolean;
  primaryTarget: ConversationTarget;
  headings: TimelineHeading[];
  headingsNextCursor: string | null;
  replies: TimelineReply[];
  threads: TimelineThread[];
}

function messageText(message: AgnoChatMessage): string {
  return message.parts
    .filter((part): part is { type: "text"; text: string } => part.type === "text")
    .map((part) => part.text)
    .join("") || message.content || "";
}

function messageCreatedAt(message: AgnoChatMessage): string {
  const value = message.metadata?.createdAt;
  return typeof value === "string" ? value : "";
}

/** Pure merge used by the hook and contract tests. Renderer output is the sole reply authority. */
export function buildLoadedTimeline(
  messages: AgnoChatMessage[],
  status: ChatStatus,
  incompleteEnd = false,
  incompleteStart = false,
): { turns: TimelineTurn[]; messageTurnIds: Map<string, string>; visibleAssistantIds: string[] } {
  // The message store uses this same comparator for the transcript's display
  // order: canonical timestamps first, then optimistic messages by localSeq.
  // Sorting a copy accepts either feed direction without changing the caller.
  const orderedMessages = [...messages].sort(compareMessages);
  const visibleAssistantIds = buildRenderItems(orderedMessages, status, { incompleteEnd })
    .filter((item) => item.kind === "message" && item.role === "assistant")
    .map((item) => item.id);
  const visibleSet = new Set(visibleAssistantIds);
  const turns: TimelineTurn[] = [];
  const messageTurnIds = new Map<string, string>();
  let current: TimelineTurn | null = null;

  for (const message of orderedMessages) {
    const indexedTurn = typeof message.metadata?.navigationTurnId === "string"
      ? message.metadata.navigationTurnId
      : null;
    if (message.role === "user") {
      const turnId = indexedTurn ?? message.id;
      current = {
        turnId,
        startMessageId: message.id,
        startedAt: messageCreatedAt(message),
        ordinal: null,
        pending: false,
        loaded: true,
        primaryTarget: { kind: "message", messageId: message.id },
        headings: [],
        headingsNextCursor: null,
        replies: [],
        threads: [],
      };
      turns.push(current);
    } else if (!current) {
      const turnId = indexedTurn ?? `loaded-prefix:${orderedMessages[0]?.id ?? message.id}`;
      current = {
        turnId,
        startMessageId: message.id,
        startedAt: messageCreatedAt(message),
        ordinal: null,
        pending: false,
        loaded: true,
        primaryTarget: { kind: "message", messageId: message.id },
        headings: [],
        headingsNextCursor: null,
        replies: [],
        threads: [],
      };
      turns.push(current);
    }
    // Placement follows the turn displayed by the transcript. A message's
    // indexed ID can belong to a turn outside this loaded window.
    const turnId = current?.turnId;
    if (turnId) messageTurnIds.set(message.id, turnId);
    if (current && message.role === "assistant" && visibleSet.has(message.id)) {
      current.replies.push({
        messageId: message.id,
        label: messageText(message).replace(/\s+/g, " ").trim().slice(0, 80),
      });
      current.primaryTarget = { kind: "message", messageId: message.id };
    }
  }
  const suppressPartialReplies = (turn: TimelineTurn | undefined) => {
    if (!turn) return;
    turn.replies = [];
    turn.pending = true;
    turn.primaryTarget = isCanonicalConversationId(turn.turnId)
      ? { kind: "turn", turnId: turn.turnId }
      : { kind: "message", messageId: turn.startMessageId };
  };
  if (incompleteStart) suppressPartialReplies(turns[0]);
  if (incompleteEnd) suppressPartialReplies(turns.at(-1));
  return { turns, messageTurnIds, visibleAssistantIds };
}

export function mergeTimelineTurns(
  indexed: NavigationTurn[],
  loaded: TimelineTurn[],
): TimelineTurn[] {
  const merged = new Map<string, TimelineTurn>();
  const loadedOrder = new Map(loaded.map((turn, index) => [turn.turnId, index]));
  indexed.forEach((turn) => merged.set(turn.turn_id, {
    turnId: turn.turn_id,
    startMessageId: turn.start_message_id,
    startedAt: turn.started_at,
    ordinal: turn.ordinal,
    pending: turn.pending,
    loaded: false,
    primaryTarget: { kind: "turn", turnId: turn.turn_id },
    headings: turn.headings.map((heading) => ({ ...heading, loaded: false })),
    headingsNextCursor: turn.headings_next_cursor,
    replies: [],
    threads: [],
  }));
  loaded.forEach((local) => {
    const server = merged.get(local.turnId);
    merged.set(local.turnId, server ? {
      ...server,
      loaded: true,
      startMessageId: local.startMessageId,
      startedAt: local.startedAt || server.startedAt,
      pending: server.pending || local.pending,
      primaryTarget: local.primaryTarget,
      replies: local.replies,
    } : {
      ...local,
      // Placement below mutates the merged turn. Never reuse a memoized
      // loaded turn: anchor/heading refreshes would append its threads again.
      threads: [],
    });
  });
  return [...merged.values()].sort((a, b) => {
    // A loaded prefix precedes the first user turn. Untimestamped optimistic
    // turns follow canonical turns, as they do in the transcript's message store.
    const prefix = Number(b.turnId.startsWith("loaded-prefix:")) - Number(a.turnId.startsWith("loaded-prefix:"));
    if (prefix) return prefix;
    if (!a.startedAt || !b.startedAt) {
      if (Boolean(a.startedAt) !== Boolean(b.startedAt)) return a.startedAt ? -1 : 1;
      const aPosition = loadedOrder.get(a.turnId);
      const bPosition = loadedOrder.get(b.turnId);
      if (aPosition !== undefined && bPosition !== undefined) return aPosition - bPosition;
    }
    return a.startedAt.localeCompare(b.startedAt) || a.startMessageId.localeCompare(b.startMessageId);
  });
}

export function useConversationNavigation({
  sessionId,
  messages,
  status,
  threadAnchors,
  getToken,
  fetchWithAuth,
  hasLoaded,
  enabled = true,
  incompleteStart = false,
  incompleteEnd = false,
  refreshNonce = 0,
  bootstrap,
  bootstrapPending = false,
}: {
  sessionId: string;
  messages: AgnoChatMessage[];
  status: ChatStatus;
  threadAnchors: ThreadAnchorRef[];
  getToken: () => Promise<string>;
  fetchWithAuth: FetchWithAuth;
  hasLoaded: boolean;
  enabled?: boolean;
  incompleteStart?: boolean;
  incompleteEnd?: boolean;
  refreshNonce?: number;
  bootstrap?: ConversationBootstrap | null;
  bootstrapPending?: boolean;
}) {
  const [capabilities, setCapabilities] = useState<NavigationCapabilities | null>(null);
  const [capabilityState, setCapabilityState] = useState<"pending" | "supported" | "unsupported" | "error">("pending");
  const [index, setIndex] = useState<SessionNavigationResponse | null>(null);
  const [indexError, setIndexError] = useState(false);
  const [loadingOlderTurns, setLoadingOlderTurns] = useState(false);
  const [activeTarget, setActiveTarget] = useState<ConversationTarget | null>(null);
  const [localHeadings, setLocalHeadings] = useState<Map<string, TimelineHeading[]>>(new Map());
  // Reuse parsing work for unchanged rows while the live assistant row grows.
  // Keep only the current visible version of each message in this cache.
  const headingCacheRef = useRef(new Map<string, {
    text: string;
    result: Promise<TimelineHeading[]>;
  }>());
  const requestRef = useRef(0);
  const initialRefreshNonce = useRef(refreshNonce);
  const backendReconnectKey = useBackendReconnectKey();

  const loaded = useMemo(
    () => buildLoadedTimeline(messages, status, incompleteEnd, incompleteStart),
    [incompleteEnd, incompleteStart, messages, status],
  );

  const loadIndex = useCallback(async (replace = true, before?: string) => {
    if (!capabilities?.turn_index) return;
    const request = ++requestRef.current;
    const controller = new AbortController();
    if (!replace) setLoadingOlderTurns(true);
    setIndexError(false);
    try {
      const page = await fetchSessionNavigation(
        fetchWithAuth,
        sessionId,
        controller.signal,
        replace ? undefined : before,
      );
      if (requestRef.current !== request) return;
      setIndex((previous) => {
        if (!previous || previous.generation !== page.generation) return page;
        const items = new Map(previous.items.map((turn) => [turn.turn_id, turn]));
        page.items.forEach((turn) => items.set(turn.turn_id, turn));
        return {
          ...page,
          items: [...items.values()].sort((a, b) => (
            a.started_at.localeCompare(b.started_at) || a.start_message_id.localeCompare(b.start_message_id)
          )),
          next_cursor: replace ? previous.next_cursor ?? page.next_cursor : page.next_cursor,
        };
      });
    } catch {
      if (requestRef.current === request) setIndexError(true);
    } finally {
      if (requestRef.current === request) setLoadingOlderTurns(false);
    }
    return () => controller.abort();
  }, [capabilities?.turn_index, fetchWithAuth, sessionId]);

  useEffect(() => {
    if (!hasLoaded || !enabled) return;
    const request = ++requestRef.current;
    const controller = new AbortController();
    setCapabilityState("pending");
    setCapabilities(null);
    setIndex(null);
    setIndexError(false);
    if (bootstrap?.session.id === sessionId) {
      capabilitiesByGeneration.set(capabilityCacheKey(backendReconnectKey), { state: "supported", capabilities: bootstrap.navigation });
      setCapabilities(bootstrap.navigation);
      setCapabilityState("supported");
      if (bootstrap.navigation_window) setIndex(bootstrap.navigation_window);
      return () => controller.abort();
    }
    if (bootstrapPending) return () => controller.abort();
    const cached = capabilitiesByGeneration.get(capabilityCacheKey(backendReconnectKey));
    if (cached) {
      if (cached.state === "supported") setCapabilities(cached.capabilities);
      setCapabilityState(cached.state);
      return () => controller.abort();
    }
    probeNavigationCapabilities(fetchWithAuth, sessionId, controller.signal).then((result) => {
      if (requestRef.current !== request) return;
      if (result.state === "supported") {
        capabilitiesByGeneration.set(capabilityCacheKey(backendReconnectKey), result);
        setCapabilities(result.capabilities);
        setCapabilityState("supported");
      } else {
        if (result.state === "unsupported") capabilitiesByGeneration.set(capabilityCacheKey(backendReconnectKey), result);
        setCapabilityState(result.state === "error" ? "error" : "unsupported");
      }
    }).catch(() => {
      if (requestRef.current === request) setCapabilityState("error");
    });
    return () => controller.abort();
  }, [backendReconnectKey, bootstrap, bootstrapPending, enabled, fetchWithAuth, hasLoaded, sessionId]);

  useEffect(() => {
    if (capabilityState === "supported" && capabilities?.turn_index
        && !(bootstrap?.navigation_window && refreshNonce === initialRefreshNonce.current)) void loadIndex(true);
  }, [capabilities?.turn_index, capabilityState, loadIndex, refreshNonce, bootstrap]);

  const visibleAssistantKey = loaded.visibleAssistantIds.join("\0");
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const byId = new Map(messages.map((message) => [message.id, message]));
    const visibleIds = new Set(loaded.visibleAssistantIds);
    const growingId = status === "streaming" || status === "submitted"
      ? loaded.visibleAssistantIds.at(-1) : undefined;
    for (const messageId of headingCacheRef.current.keys()) {
      if (!visibleIds.has(messageId)) headingCacheRef.current.delete(messageId);
    }
    void Promise.all(loaded.visibleAssistantIds.map(async (messageId) => {
      const message = byId.get(messageId);
      if (!message) return [messageId, EMPTY_HEADINGS] as const;
      const text = messageText(message);
      let cached = headingCacheRef.current.get(messageId);
      // Keep unchanged headings visible, but parse a growing reply only when
      // it completes. Every SSE frame otherwise parses the full Markdown.
      if (messageId === growingId && cached?.text !== text) return [messageId, EMPTY_HEADINGS] as const;
      if (!cached || cached.text !== text) {
        cached = {
          text,
          result: extractConversationHeadings(text).then((headings) => headings.map((heading) => ({
            message_id: messageId,
            key: heading.key,
            level: heading.level,
            ordinal: heading.ordinal,
            text: heading.text,
            content_fingerprint: heading.contentFingerprint,
            loaded: true,
          }))),
        };
        headingCacheRef.current.set(messageId, cached);
      }
      return [messageId, await cached.result] as const;
    })).then((entries) => {
      if (!cancelled) setLocalHeadings((previous) => {
        if (previous.size === entries.length && entries.every(([id, headings]) => previous.get(id) === headings)) return previous;
        return new Map(entries);
      });
    });
    return () => { cancelled = true; };
  }, [enabled, messages, status, visibleAssistantKey]);

  const children = useChildSessions(sessionId, getToken, {
    enabled,
    includeNavigation: capabilities?.child_navigation_metadata === true,
    refreshNonce,
    initialChildren: bootstrap?.children,
    pending: bootstrapPending,
  });
  const [detailOrigins, setDetailOrigins] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!enabled || bootstrapPending || (bootstrap && refreshNonce === initialRefreshNonce.current)) return;
    const missing = children.children.filter((child) => !child.source_message_id
      && !threadAnchors.some((anchor) => anchor.child_session_id === child.id));
    if (missing.length === 0) return;
    let cancelled = false;
    // Older servers omit navigation metadata and may lack the batch anchors
    // endpoint. Session detail still carries the immutable thread anchor.
    const timer = setTimeout(() => {
      void Promise.all(missing.map(async (child) => {
        try {
          const response = await fetchWithAuth(`/api/sessions/${encodeURIComponent(child.id)}`, {});
          if (!response.ok) return null;
          const detail = await response.json() as { metadata?: { thread_anchor?: { source_message_id?: unknown } } };
          const origin = detail.metadata?.thread_anchor?.source_message_id;
          return typeof origin === "string" ? [child.id, origin] as const : null;
        } catch {
          return null;
        }
      })).then((entries) => {
        if (!cancelled) {
          setDetailOrigins(new Map(entries.filter((entry): entry is readonly [string, string] => entry !== null)));
        }
      });
    }, 650);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bootstrap, bootstrapPending, children.children, enabled, fetchWithAuth, refreshNonce, sessionId, threadAnchors]);

  const turns = useMemo(() => {
    const result = mergeTimelineTurns(index?.items ?? [], loaded.turns);
    const turnMap = new Map(result.map((turn) => [turn.turnId, turn]));
    const anchorMap = new Map(threadAnchors.map((anchor) => [anchor.child_session_id, anchor]));
    for (const turn of result) {
      if (!turn.loaded || turn.pending) continue;
      const loadedMessageIds = new Set(turn.replies.map((reply) => reply.messageId));
      loadedMessageIds.add(turn.startMessageId);
      const verified: TimelineHeading[] = [];
      for (const messageId of loadedMessageIds) {
        verified.push(...(localHeadings.get(messageId) ?? []));
      }
      turn.headings = verified;
      turn.headingsNextCursor = null;
    }
    for (const child of children.children) {
      const anchor = anchorMap.get(child.id);
      const sourceMessageId = child.source_message_id ?? anchor?.source_message_id ?? detailOrigins.get(child.id) ?? null;
      const sourceTurnId = [child.source_turn_id, sourceMessageId && loaded.messageTurnIds.get(sourceMessageId)]
        .find((id) => id != null && turnMap.has(id)) ?? null;
      const turn = sourceTurnId ? turnMap.get(sourceTurnId) : undefined;
      if (turn) turn.threads.push({ child, sourceMessageId, sourceTurnId });
    }
    result.forEach((turn) => turn.threads.sort((a, b) => (
      (a.child.source_created_at ?? a.child.created_at).localeCompare(b.child.source_created_at ?? b.child.created_at)
      || a.child.id.localeCompare(b.child.id)
    )));
    return result;
  }, [children.children, detailOrigins, index?.items, loaded, localHeadings, threadAnchors]);

  const placedThreadIds = useMemo(
    () => new Set(turns.flatMap((turn) => turn.threads.map((thread) => thread.child.id))),
    [turns],
  );
  const otherThreads = useMemo(() => children.children
    .filter((child) => !placedThreadIds.has(child.id))
    .map((child) => {
      const anchor = threadAnchors.find((item) => item.child_session_id === child.id);
      return {
        child,
        sourceMessageId: child.source_message_id ?? anchor?.source_message_id ?? detailOrigins.get(child.id) ?? null,
        sourceTurnId: child.source_turn_id ?? null,
      } satisfies TimelineThread;
    }), [children.children, detailOrigins, placedThreadIds, threadAnchors]);

  useEffect(() => {
    const update = () => {
      const parsed = parseConversationTarget(window.location.hash);
      setActiveTarget(parsed.state === "valid" ? parsed.target : null);
    };
    update();
    window.addEventListener("popstate", update);
    window.addEventListener("hashchange", update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("hashchange", update);
    };
  }, [sessionId]);

  // Loaded message turns have the same anchors as the transcript and can be
  // tracked locally even when this server has no navigation index API.
  const scrollSpyEnabled = enabled && hasLoaded && loaded.turns.length > 0;
  const onVisibleTargetChange = useCallback((target: ConversationTarget) => {
    if (!scrollSpyEnabled) return;
    const match = window.location.pathname.match(/^\/session\/([^/]+)\/?$/);
    if (!match || decodeURIComponent(match[1]) !== sessionId) return;
    const fragment = conversationTargetFragment(target);
    if (window.location.hash === fragment) return;
    // replaceState does not dispatch hashchange/popstate, so this read-only
    // viewport update cannot restart deep-link positioning or add history.
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}${fragment}`);
    setActiveTarget(target);
  }, [scrollSpyEnabled, sessionId]);

  const loadMoreHeadings = useCallback(async (turnId: string) => {
    const turn = turns.find((item) => item.turnId === turnId);
    if (!turn?.headingsNextCursor) return;
    const controller = new AbortController();
    try {
      const page = await fetchTurnHeadings(
        fetchWithAuth, sessionId, turnId, controller.signal, turn.headingsNextCursor,
      );
      setIndex((previous) => previous ? {
        ...previous,
        items: previous.items.map((item) => item.turn_id === turnId ? {
          ...item,
          headings: [...item.headings, ...page.items].filter((heading, position, all) => (
            all.findIndex((candidate) => candidate.message_id === heading.message_id && candidate.key === heading.key) === position
          )),
          headings_next_cursor: page.next_cursor,
        } : item),
      } : previous);
    } catch {
      setIndexError(true);
    }
  }, [fetchWithAuth, sessionId, turns]);

  return {
    turns,
    otherThreads,
    activeTarget,
    scrollSpyEnabled,
    onVisibleTargetChange,
    capabilityState,
    indexSupported: capabilities?.turn_index === true,
    indexState: index?.state ?? null,
    indexCoverage: index?.coverage ?? null,
    indexError,
    loadingOlderTurns,
    hasMoreTurns: Boolean(index?.next_cursor),
    loadMoreTurns: () => void loadIndex(false, index?.next_cursor ?? undefined),
    retryIndex: () => void loadIndex(true),
    loadMoreHeadings,
    childrenError: children.error,
    loadingChildren: children.loading,
    loadingMoreThreads: children.loadingMore,
    hasMoreThreads: children.hasMore,
    loadMoreThreads: children.loadMore,
    retryChildren: children.refetch,
  };
}
